/**
 * GridLoadingOrchestrator
 * 
 * Coordinates multiple async operations during grid initialization and updates.
 * Provides a centralized way to track loading state, progress, and errors.
 * 
 * Usage:
 *   const orchestrator = new GridLoadingOrchestrator();
 *   orchestrator.registerOperation('load-data', 1);
 *   orchestrator.startOperation('load-data');
 *   // ... do work
 *   orchestrator.completeOperation('load-data');
 */
class GridLoadingOrchestrator {
  constructor(options = {}) {
    this.operations = new Map();
    this.listeners = [];
    this.defaultTimeout = options.defaultTimeout || 30000; // 30 seconds default
    this.timeouts = new Map(); // Track timeout handles
  }

  /**
   * Register an operation to be tracked
   * @param {string} name - Unique operation identifier
   * @param {number} priority - Operation priority (lower = higher priority)
   */
  registerOperation(name, priority = 0) {
    this.operations.set(name, {
      status: 'pending',
      priority,
      startTime: null,
      endTime: null,
      error: null
    });
    this.notifyListeners();
  }

  /**
   * Mark operation as started
   * @param {string} name - Operation identifier
   * @param {number} timeout - Optional timeout in milliseconds (defaults to defaultTimeout)
   */
  startOperation(name, timeout = null) {
    const op = this.operations.get(name);
    if (op) {
      op.status = 'loading';
      op.startTime = Date.now();
      this.notifyListeners();
      
      // Set up timeout for this operation
      const timeoutMs = timeout !== null ? timeout : this.defaultTimeout;
      if (timeoutMs > 0) {
        // Clear any existing timeout for this operation before scheduling a new one
        this.clearOperationTimeout(name);
        const timeoutHandle = setTimeout(() => {
          const operation = this.operations.get(name);
          if (operation && operation.status === 'loading') {
            const timeoutError = new Error(`Operation "${name}" timed out after ${timeoutMs}ms`);
            timeoutError.isTimeout = true;
            this.failOperation(name, timeoutError);
          }
        }, timeoutMs);
        this.timeouts.set(name, timeoutHandle);
      }
    } else {
      console.warn(`Operation "${name}" not registered. Registering now.`);
      this.registerOperation(name);
      this.startOperation(name, timeout);
    }
  }

  /**
   * Mark operation as complete
   * @param {string} name - Operation identifier
   */
  completeOperation(name) {
    const op = this.operations.get(name);
    if (op) {
      op.status = 'complete';
      op.endTime = Date.now();
      
      // Clear timeout if exists
      this.clearOperationTimeout(name);
      
      this.notifyListeners();
    } else {
      console.warn(`Operation "${name}" not found`);
    }
  }

  /**
   * Mark operation as failed
   * @param {string} name - Operation identifier
   * @param {Error} error - Error object
   */
  failOperation(name, error) {
    const op = this.operations.get(name);
    if (op) {
      op.status = 'failed';
      op.error = error;
      op.endTime = Date.now();
      
      // Clear timeout if exists
      this.clearOperationTimeout(name);
      
      this.notifyListeners();
    } else {
      console.warn(`Operation "${name}" not found`);
    }
  }

  /**
   * Clear timeout for an operation
   * @param {string} name - Operation identifier
   */
  clearOperationTimeout(name) {
    const timeoutHandle = this.timeouts.get(name);
    if (timeoutHandle) {
      clearTimeout(timeoutHandle);
      this.timeouts.delete(name);
    }
  }

  /**
   * Check if all operations are complete
   * @returns {boolean}
   */
  isComplete() {
    if (this.operations.size === 0) return true;
    return Array.from(this.operations.values())
      .every(op => op.status === 'complete' || op.status === 'failed');
  }

  /**
   * Check if any operations are currently loading
   * @returns {boolean}
   */
  isLoading() {
    // Only count 'loading' (actively in-flight) as loading.
    // 'pending' means registered-but-not-yet-started, which is normal during
    // sequential load phases and should not block the UI indefinitely.
    return Array.from(this.operations.values())
      .some(op => op.status === 'loading');
  }

  /**
   * Get loading progress (0-100)
   * @returns {number}
   */
  getProgress() {
    const total = this.operations.size;
    if (total === 0) return 100;
    
    const complete = Array.from(this.operations.values())
      .filter(op => op.status === 'complete' || op.status === 'failed').length;
    return Math.round((complete / total) * 100);
  }

  /**
   * Get currently loading operation (highest priority)
   * @returns {string|null}
   */
  getCurrentOperation() {
    const loadingOps = Array.from(this.operations.entries())
      .filter(([_, op]) => op.status === 'loading')
      .sort((a, b) => a[1].priority - b[1].priority);
    
    return loadingOps.length > 0 ? loadingOps[0][0] : null;
  }

  /**
   * Get all failed operations
   * @returns {Array<{name: string, error: Error}>}
   */
  getFailedOperations() {
    return Array.from(this.operations.entries())
      .filter(([_, op]) => op.status === 'failed')
      .map(([name, op]) => ({ name, error: op.error }));
  }

  /**
   * Subscribe to loading state changes
   * @param {Function} listener - Callback function
   * @returns {Function} Unsubscribe function
   */
  subscribe(listener) {
    this.listeners.push(listener);
    // Immediately notify with current state
    this.notifyListeners();
    
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  /**
   * Notify all listeners of state change
   */
  notifyListeners() {
    const state = {
      isLoading: this.isLoading(),
      isComplete: this.isComplete(),
      progress: this.getProgress(),
      currentOperation: this.getCurrentOperation(),
      operations: Object.fromEntries(this.operations),
      failedOperations: this.getFailedOperations()
    };
    
    this.listeners.forEach(listener => {
      try {
        listener(state);
      } catch (error) {
        console.error('Error in loading orchestrator listener:', error);
      }
    });
  }

  /**
   * Reset all operations
   */
  reset() {
    // Clear all timeouts
    this.timeouts.forEach((handle) => clearTimeout(handle));
    this.timeouts.clear();
    
    this.operations.clear();
    this.notifyListeners();
  }

  /**
   * Get operation details
   * @param {string} name - Operation identifier
   * @returns {Object|null}
   */
  getOperation(name) {
    return this.operations.get(name) || null;
  }

  /**
   * Get all operations as array
   * @returns {Array<{name: string, ...operation}>}
   */
  getAllOperations() {
    return Array.from(this.operations.entries())
      .map(([name, op]) => ({ name, ...op }))
      .sort((a, b) => a.priority - b.priority);
  }
}

export default GridLoadingOrchestrator;
